import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { Navbar } from "../../components/Navbar";
import { LanguageToggle } from "../../components/LanguageToggle";
import { Title } from "../../components/Title";
import { LoadingSpinner } from "../../components/LoadingSpinner";
import { InfoBox } from "../../components/InfoBox";
import { Page } from "../../components/Page";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import colors from "@styling/colors";
import { useCompleteBookkeepingOAuthMutation } from "../../query/bookkeepingIntegrationQuery";
import { captureException } from "@sentry/react";

const ContentWrapper = styled.div`
  max-width: 500px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.x3}px;
  text-align: center;
`;

const Message = styled.p`
  margin: 0;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
`;

const SpirisIntegration = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { mutate, isError: isMutationError, error: mutationError, isPending } =
    useCompleteBookkeepingOAuthMutation();

  const error = searchParams.get("error");
  const errorDescription = searchParams.get("error_description");
  const isError = !!error || isMutationError;

  const errorMessage = useMemo(() => {
    if (error) {
      if (error === "invalid_scope") {
        return t("bookkeeping.invalidScope");
      }
      captureException(new Error(error), {
        extra: {
          error,
          errorDescription,
        },
        tags: {
          integrationType: "SPIRIS",
        },
      });
    }
    if (errorDescription) {
      return errorDescription;
    }
    if (isMutationError) {
      return mutationError?.message || t("bookkeeping.unknownError");
    }
    return t("bookkeeping.unknownError");
  }, [error, errorDescription, isMutationError, mutationError, t]);

  useEffect(() => {
    if (isError) {
      return;
    }
    const state = searchParams.get("state");
    const code = searchParams.get("code");
    if (!state || !code) {
      captureException(new Error("Missing state or code"), {
        extra: {
          state,
          code,
        },
        tags: {
          integrationType: "SPIRIS",
        },
      });
      return;
    }
    if (isPending) {
      return;
    }
    mutate({
      integrationType: "SPIRIS",
      code,
      state,
    }, {
      onSuccess: () => {
        console.log("onSuccess");

      },
      onError: (e) => {
        console.log("onError", e);
      },
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  return (
    <Page centered navbar={<Navbar><LanguageToggle /></Navbar>}>
      <ContentWrapper>
        {isError ? (
          <>
            <Title>{t("bookkeeping.connectionFailed")}</Title>
            <InfoBox variant="error">{errorMessage}</InfoBox>
          </>
        ) : (
          <>
            {isPending ? (
              <>
                <Title>{t("bookkeeping.connecting")}</Title>
                <LoadingSpinner centered />
                <Message>{t("bookkeeping.pleaseWait")}</Message>
              </>
            ) : (
              <>
                <Title>{t("bookkeeping.completedTitle")}</Title>
                <InfoBox variant="success">{t("bookkeeping.connectedSuccess")}</InfoBox>
              </>
            )}
          </>
        )}
      </ContentWrapper>
    </Page>
  );
};

export default SpirisIntegration;

